import { useState, useEffect, useMemo } from "react";
import { Loader2, Shield, CheckCircle2, AlertCircle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { PhoneInput } from "@/components/ui/phone-input";
import { Label } from "@/components/ui/label";
import { ArrowRight, Plus, Trash2, User } from "lucide-react";
import { toast } from "@/hooks/use-toast";
import { useDebounce } from "@/hooks/use-debounce";
import { emailValidationService } from "@/services/emailValidationService";

export interface TitleHolder {
  full_name: string;
  email: string;
  phone_number: string;
}

type EmailStatus = 'idle' | 'checking' | 'valid' | 'conflict' | 'duplicate';

interface EmailValidation {
  status: EmailStatus;
  message: string;
}

interface TitleHoldersStepProps {
  titleHolders: TitleHolder[];
  onChange: (holders: TitleHolder[]) => void;
  onNext: () => void;
  isSubmitting?: boolean;
}

const emptyHolder = (): TitleHolder => ({
  full_name: "",
  email: "",
  phone_number: "",
});

/**
 * Hook to debounce an array of email strings individually
 */
const useDebouncedEmails = (holders: TitleHolder[], delay: number) => {
  // We debounce the entire serialized email list
  const emailsKey = holders.map(h => h.email).join('|');
  const debouncedKey = useDebounce(emailsKey, delay);
  return useMemo(() => debouncedKey.split('|'), [debouncedKey]);
};

const TitleHoldersStep = ({ titleHolders, onChange, onNext, isSubmitting = false }: TitleHoldersStepProps) => {
  const holders = titleHolders.length > 0 ? titleHolders : [emptyHolder()];
  const [emailValidations, setEmailValidations] = useState<Record<number, EmailValidation>>({});

  const debouncedEmails = useDebouncedEmails(holders, 500);

  // Validate emails when debounced values change
  useEffect(() => {
    debouncedEmails.forEach((email, index) => {
      if (!email || !email.trim()) {
        setEmailValidations(prev => {
          const next = { ...prev };
          delete next[index];
          return next;
        });
        return;
      }

      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(email)) {
        return; // Don't validate malformed emails
      }

      // Check for duplicates within the form
      const duplicateIndex = debouncedEmails.findIndex(
        (e, i) => i !== index && e.toLowerCase().trim() === email.toLowerCase().trim()
      );
      if (duplicateIndex !== -1 && duplicateIndex < index) {
        setEmailValidations(prev => ({
          ...prev,
          [index]: { status: 'duplicate', message: 'This email is already used by another title holder above' }
        }));
        return;
      }

      // Check against existing accounts
      setEmailValidations(prev => ({
        ...prev,
        [index]: { status: 'checking', message: '' }
      }));

      emailValidationService.checkEmailExists(email).then(result => {
        setEmailValidations(prev => {
          // Only update if the email hasn't changed since we started checking
          if (debouncedEmails[index] !== email) return prev;

          if (result.error) {
            return { ...prev, [index]: { status: 'idle', message: '' } };
          }

          if (result.exists && (result.userType === 'seller' || result.userType === 'buyer')) {
            return {
              ...prev,
              [index]: {
                status: 'conflict',
                message: `This email is already registered as a ${result.userType}. Please use a different email.`
              }
            };
          }

          return { ...prev, [index]: { status: 'valid', message: '' } };
        });
      });
    });

    // Clean up validations for removed holders
    setEmailValidations(prev => {
      const next = { ...prev };
      Object.keys(next).forEach(key => {
        if (Number(key) >= debouncedEmails.length) {
          delete next[Number(key)];
        }
      });
      return next;
    });
  }, [debouncedEmails]);

  const updateHolder = (index: number, field: keyof TitleHolder, value: string) => {
    const updated = [...holders];
    updated[index] = { ...updated[index], [field]: value };
    onChange(updated);
  };

  const addHolder = () => {
    if (holders.length >= 5) {
      toast({ title: "Maximum 5 title holders allowed", variant: "destructive" });
      return;
    }
    onChange([...holders, emptyHolder()]);
  };

  const removeHolder = (index: number) => {
    if (holders.length <= 1) return;
    onChange(holders.filter((_, i) => i !== index));
  };

  const hasEmailIssues = Object.values(emailValidations).some(
    v => v.status === 'conflict' || v.status === 'duplicate' || v.status === 'checking'
  );

  const validate = (): boolean => {
    for (const h of holders) {
      if (!h.full_name.trim() || !h.email.trim() || !h.phone_number.trim()) {
        toast({ title: "Please fill in all fields for each title holder", variant: "destructive" });
        return false;
      }
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(h.email)) {
        toast({ title: `Invalid email for ${h.full_name || "title holder"}`, variant: "destructive" });
        return false;
      }
    }
    if (hasEmailIssues) {
      toast({ title: "Please resolve email issues before continuing", variant: "destructive" });
      return false;
    }
    return true;
  };

  const handleNext = () => {
    if (validate()) onNext();
  };

  const renderEmailFeedback = (index: number) => {
    const validation = emailValidations[index];
    if (!validation) return null;

    if (validation.status === 'checking') {
      return (
        <div className="flex items-center gap-1.5 text-xs text-muted-foreground mt-1">
          <Loader2 className="h-3 w-3 animate-spin" />
          <span>Checking email...</span>
        </div>
      );
    }

    if (validation.status === 'valid') {
      return (
        <div className="flex items-center gap-1.5 text-xs text-emerald-400 mt-1">
          <CheckCircle2 className="h-3 w-3" />
          <span>Email available</span>
        </div>
      );
    }

    if (validation.status === 'conflict' || validation.status === 'duplicate') {
      return (
        <div className="flex items-center gap-1.5 text-xs text-destructive mt-1">
          <AlertCircle className="h-3 w-3" />
          <span>{validation.message}</span>
        </div>
      );
    }

    return null;
  };

  return (
    <div className="space-y-6">
      <div className="text-center mb-8">
        <h2 className="text-2xl sm:text-3xl font-bold text-white mb-2">
          Who's on the Title?
        </h2>
        <p className="text-slate-400">
          Enter the details for each person on the property title. We'll need everyone's info for the marketing agreement.
        </p>
      </div>

      {/* Trust notice */}
      <div className="flex items-center justify-center gap-2 text-xs text-slate-500">
        <Shield className="h-3.5 w-3.5 text-slate-500" />
        <span>Your information is securely encrypted and never sold to third parties.</span>
      </div>

      <div className="space-y-6">
        {holders.map((holder, index) => (
          <div
            key={index}
            className="bg-card border border-border rounded-lg p-5 space-y-4"
          >
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2 text-sm font-semibold text-muted-foreground">
                <User className="h-4 w-4" />
                Title Holder {index + 1}
              </div>
              {holders.length > 1 && (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => removeHolder(index)}
                  className="text-destructive hover:text-destructive/80 h-8 w-8 p-0"
                >
                  <Trash2 className="h-4 w-4" />
                </Button>
              )}
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-[1fr_1fr_1.5fr] gap-4">
              <div className="space-y-2">
                <Label htmlFor={`name-${index}`} className="text-foreground">Full Legal Name *</Label>
                <Input
                  id={`name-${index}`}
                  placeholder="John A. Smith"
                  value={holder.full_name}
                  onChange={(e) => updateHolder(index, "full_name", e.target.value)}
                  maxLength={100}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor={`email-${index}`} className="text-foreground">Email *</Label>
                <Input
                  id={`email-${index}`}
                  type="email"
                  placeholder="john@example.com"
                  value={holder.email}
                  onChange={(e) => updateHolder(index, "email", e.target.value)}
                  maxLength={255}
                  className={
                    emailValidations[index]?.status === 'conflict' || emailValidations[index]?.status === 'duplicate'
                      ? 'border-destructive'
                      : emailValidations[index]?.status === 'valid'
                        ? 'border-emerald-500'
                        : ''
                  }
                />
                {renderEmailFeedback(index)}
              </div>
              <div className="space-y-2 sm:col-span-2 lg:col-span-1">
                <Label htmlFor={`phone-${index}`} className="text-foreground">Phone *</Label>
                <PhoneInput
                  id={`phone-${index}`}
                  placeholder="(555) 123-4567"
                  value={holder.phone_number}
                  onChange={(value) => updateHolder(index, "phone_number", value)}
                  showValidation={true}
                  maxLength={20}
                />
              </div>
            </div>
          </div>
        ))}
      </div>

      <Button
        variant="outline"
        onClick={addHolder}
        className="w-full border-dashed border-border text-muted-foreground hover:text-foreground"
      >
        <Plus className="h-4 w-4 mr-2" />
        Add Another Person on Title
      </Button>

      <div className="flex justify-end pt-4">
        <Button
          onClick={handleNext}
          disabled={isSubmitting || hasEmailIssues}
          className="bg-[#ffd700] text-[#080c14] hover:bg-[#e6c200] px-8 group"
          size="lg"
        >
          {isSubmitting ? (
            <Loader2 className="h-5 w-5 animate-spin" />
          ) : (
            <>
              Continue to Property Details
              <ArrowRight className="ml-2 h-5 w-5 transition-transform group-hover:translate-x-1" />
            </>
          )}
        </Button>
      </div>
    </div>
  );
};

export default TitleHoldersStep;
